import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Card,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

import EditRounded from "@mui/icons-material/EditRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import DarkModeOutlined from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import NotificationsNoneRounded from "@mui/icons-material/NotificationsNoneRounded";
import DataUsageRounded from "@mui/icons-material/DataUsageRounded";
import PersonRounded from "@mui/icons-material/PersonRounded";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import AssignmentIndOutlined from "@mui/icons-material/AssignmentIndOutlined";
import ContactEmergencyOutlined from "@mui/icons-material/ContactEmergencyOutlined";
import LinkOffRounded from "@mui/icons-material/LinkOffRounded";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import BugReportOutlined from "@mui/icons-material/BugReportOutlined";
import QrCode2Rounded from "@mui/icons-material/QrCode2Rounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";

import { PairDeviceDialog } from "@/components/PairDeviceDialog";

import type { BrokerAccount } from "@/data/mock-data";
import { getAccountMeta, USER_PROFILE } from "@/data/mock-profile";
import { useThemeStore } from "@/store/theme-store";
import { useAuthStore } from "@/store/auth-store";
import { useDevFlagsStore } from "@/store/dev-flags-store";
import { useAccountNicknamesStore } from "@/store/account-nicknames-store";

// ---- Props ---------------------------------------------------------------

interface SettingsViewProps {
  /**
   * Single account to configure. Settings is a per-account surface —
   * aggregate ("All Live") mode is handled upstream with a placeholder
   * so this component can always assume a concrete account is selected.
   */
  account: BrokerAccount;
}

// ---- Component -----------------------------------------------------------

/**
 * Settings tab — merges the WHB legacy "Configure" fields (nickname,
 * broker, broker account id, account type, target APY) with the
 * Robinhood Settings shape (Personal information, Notifications, App
 * appearance, Data usage, Deactivate). Beneficiaries is intentionally
 * dropped — not in scope for the demo and adds a legal surface we
 * don't want to stub.
 *
 * Structure:
 *   1. "This account"      — broker / nickname / id / type / APY target
 *   2. "Personal info"     — name, email, phone, address, investor,
 *                            trusted contact (read-only, tap opens a
 *                            detail dialog — stubbed for the demo)
 *   3. "Preferences"       — notifications, app appearance (dark/light
 *                            toggle, wired to the real theme store),
 *                            data usage
 *   4. "Danger zone"       — disconnect broker, deactivate account
 *
 * Copy tone matches Robinhood: short labels, sublabels describe what
 * the row controls. Destructive actions live at the bottom with
 * muted-red styling so they're discoverable but not primary.
 */
export function SettingsView({ account }: SettingsViewProps) {
  // For mock accounts we get a richer record (long broker-account UUID
  // distinct from the masked number, etc.) via the seeded meta table.
  // For real (API-driven) accounts that table has no entry — we read
  // straight from the `BrokerAccount` so the rows match the header
  // (nickname/broker shown in the selector are `account.name` /
  // `account.provider`). Sergey 2026-05-13: previously this page
  // showed "Account" / "Broker" for live accounts because the meta
  // fallback synthesises generic strings when `ACCOUNTS` doesn't have
  // the id — `account` (already passed in) has the right values.
  const meta = getAccountMeta(account.id);
  // `account.name` already reflects any nickname override (the
  // useRealAccountsAdapted hook applies them at the mapping
  // boundary), so we just read it directly. The store is still used
  // for *writes* below so an edit here propagates to every other
  // surface showing the account name.
  const displayNickname = account.name ?? meta.nickname;
  const displayBroker = account.provider ?? meta.brokerLabel;
  const displayMasked = account.masked ?? meta.accountNumberMasked;
  const displayAccountType: "Live" | "Paper" =
    account.paper ? "Paper" : "Live";
  const brokerLoginUrl = getBrokerLoginUrl(displayBroker);
  const { mode: themeMode, toggle: toggleTheme } = useThemeStore();
  // Developer card is only relevant under a real-auth session — demo
  // accounts have nothing to probe against and the panel would 401
  // every request.
  const authKind = useAuthStore((s) => s.kind);
  const debugPanelEnabled = useDevFlagsStore((s) => s.debugPanelEnabled);
  const [pairDialogOpen, setPairDialogOpen] = useState(false);
  const toggleDebugPanel = useDevFlagsStore((s) => s.toggleFlag);

  // Nickname is persisted in localStorage via the nicknames store and
  // applied to the live account list at fetch time. The dialog edits
  // a draft; on save we push to the store, which re-renders the
  // header/selector/etc with the new label.
  const setNicknameOverride = useAccountNicknamesStore((s) => s.setNickname);
  const [nicknameDialogOpen, setNicknameDialogOpen] = useState(false);
  const [draftNickname, setDraftNickname] = useState(displayNickname);

  // Target APY edit dialog — mirrors the existing Target chip editor
  // on the chart, but scoped to the Settings tab. Also demo-only.
  const [apy, setApy] = useState(account.targetApyPct);
  const [apyDialogOpen, setApyDialogOpen] = useState(false);
  const [draftApy, setDraftApy] = useState(String(apy));

  const openNicknameEditor = () => {
    setDraftNickname(displayNickname);
    setNicknameDialogOpen(true);
  };
  const saveNickname = () => {
    // Writing an empty string clears the override (account falls back
    // to the API name) — handy escape hatch for users who change
    // their mind. Save unconditionally so even "" round-trips.
    setNicknameOverride(account.id, draftNickname);
    setNicknameDialogOpen(false);
  };

  const openApyEditor = () => {
    setDraftApy(String(apy));
    setApyDialogOpen(true);
  };
  const saveApy = () => {
    const n = Number(draftApy);
    if (Number.isFinite(n) && n >= 0 && n <= 200) setApy(n);
    setApyDialogOpen(false);
  };

  const copyBrokerId = () => {
    // Best-effort — navigator.clipboard isn't available in every
    // embedded/webview context but we don't want the demo to throw.
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      void navigator.clipboard.writeText(meta.brokerAccountId);
    }
  };

  return (
    <Box sx={{ pt: 1 }}>
      {/* ---- This account ---------------------------------------- */}
      <SectionHeader>This account</SectionHeader>
      <Card sx={{ p: 0, mb: 3 }}>
        <List disablePadding>
          <DataRow
            label="Nickname"
            value={displayNickname}
            onClick={openNicknameEditor}
            action={<EditRounded sx={{ fontSize: 18, color: "text.secondary" }} />}
          />
          <Divider component="li" />
          <DataRow label="Broker" value={displayBroker} />
          <Divider component="li" />
          <DataRow
            label="Broker account ID"
            value={meta.brokerAccountId}
            valueMono
            onClick={copyBrokerId}
            action={
              <ContentCopyRounded sx={{ fontSize: 16, color: "text.secondary" }} />
            }
          />
          <Divider component="li" />
          <DataRow label="Account number" value={displayMasked} valueMono />
          <Divider component="li" />
          <DataRow
            label="Account type"
            value={
              <TypeChip type={displayAccountType} />
            }
          />
          <Divider component="li" />
          {/* Broker login shortcut — handy for popping over to the
           *  broker's web app to do things WHB doesn't expose (statements,
           *  tax docs, fund transfers). External link so the PWA itself
           *  stays in place; opens in a new tab and gets rel=noopener so
           *  the broker page can't reach back into our window. */}
          {brokerLoginUrl && (
            <>
              <DataRow
                label={`${displayBroker} login`}
                sublabel="Opens the broker's web app in a new tab"
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.open(brokerLoginUrl, "_blank", "noopener,noreferrer");
                  }
                }}
                action={
                  <OpenInNewRounded sx={{ fontSize: 18, color: "text.secondary" }} />
                }
              />
              <Divider component="li" />
            </>
          )}
          {/* Target APY — the green reference line on the Portfolio
           *  vs SPY chart. Rehomed here from the chart's settings chip
           *  so the full config surface lives in one place, and the
           *  chart just reads the store value. */}
          <DataRow
            label="Target APY"
            sublabel="Reference line on the Portfolio vs SPY chart"
            value={`${apy}%`}
            onClick={openApyEditor}
            action={<EditRounded sx={{ fontSize: 18, color: "text.secondary" }} />}
            icon={<TrendingUpRounded />}
          />
        </List>
      </Card>

      {/* ---- Personal information --------------------------------- */}
      <SectionHeader>Personal information</SectionHeader>
      <Card sx={{ p: 0, mb: 3 }}>
        <List disablePadding>
          <DataRow
            icon={<PersonRounded />}
            label="Name"
            value={USER_PROFILE.name}
          />
          <Divider component="li" />
          <DataRow
            icon={<EmailOutlined />}
            label="Email"
            value={USER_PROFILE.email}
            valueWrap
            action={
              USER_PROFILE.emailVerified ? <VerifiedChip /> : undefined
            }
          />
          <Divider component="li" />
          <DataRow
            icon={<PhoneOutlined />}
            label="Phone number"
            value={USER_PROFILE.phone}
            action={
              USER_PROFILE.phoneVerified ? <VerifiedChip /> : undefined
            }
          />
          <Divider component="li" />
          <DataRow
            icon={<PlaceOutlined />}
            label="Address"
            value={
              <Stack spacing={0.25}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {USER_PROFILE.address.line1}
                  {USER_PROFILE.address.line2
                    ? `, ${USER_PROFILE.address.line2}`
                    : ""}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {USER_PROFILE.address.cityStateZip}
                </Typography>
              </Stack>
            }
            valueWrap
          />
          <Divider component="li" />
          <DataRow
            icon={<AssignmentIndOutlined />}
            label="Investor profile"
            sublabel={USER_PROFILE.investorProfile}
            chevron
          />
          <Divider component="li" />
          <DataRow
            icon={<ContactEmergencyOutlined />}
            label="Trusted contact"
            sublabel={USER_PROFILE.trustedContact ?? "None listed"}
            chevron
          />
        </List>
      </Card>

      {/* ---- Preferences ------------------------------------------ */}
      <SectionHeader>Preferences</SectionHeader>
      <Card sx={{ p: 0, mb: 3 }}>
        <List disablePadding>
          <DataRow
            icon={<NotificationsNoneRounded />}
            label="Notifications"
            sublabel="Order fills, dividend credits, bot status alerts"
            chevron
          />
          <Divider component="li" />
          <DataRow
            icon={
              themeMode === "dark" ? (
                <LightModeOutlined />
              ) : (
                <DarkModeOutlined />
              )
            }
            label="App appearance"
            sublabel={themeMode === "dark" ? "Dark mode" : "Light mode"}
            onClick={toggleTheme}
            action={
              <Chip
                label={themeMode === "dark" ? "Dark" : "Light"}
                size="small"
                sx={{ height: 22, fontWeight: 600, fontSize: 11 }}
              />
            }
          />
          <Divider component="li" />
          <DataRow
            icon={<DataUsageRounded />}
            label="Data usage"
            sublabel="Reduce chart polling on mobile networks"
            chevron
          />
        </List>
      </Card>

      {/* ---- Developer (real-auth only) --------------------------- */}
      {authKind === "real" && (
        <>
          <SectionHeader>Developer</SectionHeader>
          <Card sx={{ p: 0, mb: 3 }}>
            <List disablePadding>
              <DataRow
                icon={<BugReportOutlined />}
                label="Debug panel"
                sublabel="Show the /api probe on the Accounts page"
                onClick={() => toggleDebugPanel("debugPanelEnabled")}
                action={
                  <Switch
                    edge="end"
                    checked={debugPanelEnabled}
                    onChange={() => toggleDebugPanel("debugPanelEnabled")}
                    // Let the row's onClick handle the toggle so taps on the
                    // switch itself don't fire two store updates.
                    onClick={(e) => e.stopPropagation()}
                  />
                }
              />
              <Divider component="li" />
              <DataRow
                icon={<QrCode2Rounded />}
                label="Pair another device"
                sublabel="Sign in your phone by scanning a QR from this screen"
                onClick={() => setPairDialogOpen(true)}
                chevron
              />
            </List>
          </Card>
        </>
      )}

      {/* ---- Danger zone ------------------------------------------ */}
      {/* Deactivate Account row removed 2026-05-13 — WHB doesn't own
       *  the actual broker account, so we can't truly close it from
       *  here. Disconnect is the only safe destructive action: unlinks
       *  the WHB ↔ broker integration but leaves the broker account
       *  intact. */}
      <SectionHeader>Danger zone</SectionHeader>
      <Card sx={{ p: 0, mb: 3 }}>
        <List disablePadding>
          <DangerRow
            icon={<LinkOffRounded />}
            label="Disconnect broker"
            sublabel={`Unlink ${displayBroker} from this WHB account`}
          />
        </List>
      </Card>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", textAlign: "center", mt: 4, mb: 2 }}
      >
        Settings shown here are demo-only. Edits persist for this session
        and reset on reload.
      </Typography>

      {/* ---- Nickname edit dialog -------------------------------- */}
      <Dialog
        open={nicknameDialogOpen}
        onClose={() => setNicknameDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Edit nickname</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={draftNickname}
            onChange={(e) => setDraftNickname(e.target.value)}
            placeholder="e.g. Retirement, Swing trading"
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Only you see this name. The broker-side label ({displayBroker}) stays unchanged.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNicknameDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveNickname}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ---- Target APY edit dialog ------------------------------ */}
      <Dialog
        open={apyDialogOpen}
        onClose={() => setApyDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Set target APY</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            type="number"
            value={draftApy}
            onChange={(e) => setDraftApy(e.target.value)}
            inputProps={{ min: 0, max: 200, step: 1 }}
            sx={{ mt: 1 }}
            InputProps={{ endAdornment: <Typography variant="body2">%</Typography> }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            Shown as the green reference line on the Portfolio vs SPY chart — in % mode as a flat
            line, in $ mode as a compounding curve.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApyDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveApy}>Save</Button>
        </DialogActions>
      </Dialog>

      <PairDeviceDialog
        open={pairDialogOpen}
        onClose={() => setPairDialogOpen(false)}
      />
    </Box>
  );
}

// ---- Subcomponents -------------------------------------------------------

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      sx={{
        display: "block",
        fontWeight: 700,
        fontSize: "0.75rem",
        letterSpacing: 0.6,
        color: "text.secondary",
        mb: 1,
        mt: 1
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * Generic settings row. Flexible enough to cover both the "label →
 * value" style (broker details) and the "icon → label + sublabel →
 * chevron" style (personal info links).
 *
 * Render contract:
 *   • If `onClick` is passed, the whole row becomes a ListItemButton
 *     and a chevron renders unless `action` overrides it.
 *   • Otherwise the row is static text — useful for read-only fields.
 *   • `value` can be a string or a ReactNode; ReactNode lets callers
 *     inject chips, multi-line addresses, etc.
 */
function DataRow({
  icon,
  label,
  sublabel,
  value,
  valueMono = false,
  valueWrap = false,
  action,
  chevron = false,
  onClick
}: {
  icon?: React.ReactNode;
  label: string;
  sublabel?: string;
  value?: React.ReactNode;
  valueMono?: boolean;
  valueWrap?: boolean;
  action?: React.ReactNode;
  chevron?: boolean;
  onClick?: () => void;
}) {
  const content = (
    <>
      {icon && (
        <Box
          sx={{
            color: "text.secondary",
            mr: 1.5,
            display: "flex",
            alignItems: "center",
            "& svg": { fontSize: 20 }
          }}
        >
          {icon}
        </Box>
      )}
      <ListItemText
        sx={{ my: 0, minWidth: 0 }}
        primary={
          <Stack
            direction={valueWrap ? "column" : "row"}
            justifyContent="space-between"
            alignItems={valueWrap ? "flex-start" : "center"}
            spacing={valueWrap ? 0.5 : 1}
          >
            <Typography
              variant="body1"
              sx={{
                // Apr-2026 typography sweep: body1 (1rem) so every
                // clickable/label row reads at the same scale as the
                // Buying Power row and the tab strip.
                fontWeight: 500,
                color: "text.secondary",
                minWidth: 0
              }}
            >
              {label}
            </Typography>
            {value !== undefined && (
              <Box
                sx={{
                  minWidth: 0,
                  textAlign: valueWrap ? "left" : "right",
                  overflow: "hidden"
                }}
              >
                {typeof value === "string" || typeof value === "number" ? (
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      fontFamily: valueMono
                        ? "ui-monospace, SFMono-Regular, Menlo, monospace"
                        : undefined,
                      // Mono rows stay a hair smaller — account numbers
                      // are long strings and pushing them to 1rem makes
                      // the right side overflow on mobile.
                      fontSize: valueMono ? 13 : undefined,
                      wordBreak: valueWrap ? "break-word" : "normal",
                      whiteSpace: valueWrap ? "normal" : "nowrap",
                      overflow: valueWrap ? "visible" : "hidden",
                      textOverflow: valueWrap ? "clip" : "ellipsis"
                    }}
                  >
                    {value}
                  </Typography>
                ) : (
                  value
                )}
              </Box>
            )}
          </Stack>
        }
        secondary={
          sublabel ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.75rem", display: "block", mt: 0.25 }}
            >
              {sublabel}
            </Typography>
          ) : null
        }
      />
      {action && (
        <Box sx={{ ml: 1, display: "flex", alignItems: "center" }}>{action}</Box>
      )}
      {chevron && !action && (
        <KeyboardArrowRight sx={{ color: "text.secondary", ml: 1 }} />
      )}
    </>
  );

  const rowSx = { px: 2, py: 1.5, alignItems: "flex-start" } as const;

  if (onClick) {
    return (
      <ListItem disablePadding>
        <ListItemButton onClick={onClick} sx={rowSx}>
          {content}
        </ListItemButton>
      </ListItem>
    );
  }

  return (
    <ListItem sx={rowSx}>
      {content}
    </ListItem>
  );
}

/** Destructive action row — muted red icon + label. */
function DangerRow({
  icon,
  label,
  sublabel
}: {
  icon: React.ReactNode;
  label: string;
  sublabel: string;
}) {
  return (
    <ListItem disablePadding>
      <ListItemButton
        onClick={() => {}}
        sx={{
          px: 2,
          py: 1.75,
          alignItems: "flex-start"
        }}
      >
        <Box
          sx={{
            color: "error.main",
            mr: 1.5,
            display: "flex",
            alignItems: "center",
            "& svg": { fontSize: 20 }
          }}
        >
          {icon}
        </Box>
        <ListItemText
          sx={{ my: 0 }}
          primary={
            <Typography variant="body1" sx={{ fontWeight: 600, color: "error.main" }}>
              {label}
            </Typography>
          }
          secondary={
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.75rem", display: "block", mt: 0.25 }}
            >
              {sublabel}
            </Typography>
          }
        />
        <KeyboardArrowRight sx={{ color: "text.secondary", ml: 1 }} />
      </ListItemButton>
    </ListItem>
  );
}

/**
 * Live/Paper chip. Matches the palette used by the account selector
 * badges — green-ish tint for Live, neutral grey for Paper so users
 * instinctively know which account moves real money.
 */
function TypeChip({ type }: { type: "Live" | "Paper" }) {
  return (
    <Chip
      size="small"
      label={type}
      sx={(t: Theme) => ({
        height: 22,
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: 0.3,
        bgcolor:
          type === "Live"
            ? alpha(t.palette.success.main, 0.15)
            : alpha(t.palette.text.primary, 0.08),
        color:
          type === "Live" ? t.palette.success.main : t.palette.text.primary
      })}
    />
  );
}

/**
 * Map a broker display name to the URL where the user can log in to
 * the broker's own web app. Used for the "<Broker> login" shortcut in
 * the "This account" card so the user can pop over to e.g. Alpaca's
 * site without leaving WHB to dig up the URL.
 *
 * Returns `null` for brokers we don't have a known login URL for (the
 * row is hidden in that case rather than linking somewhere wrong).
 * Match is case-insensitive on the broker name we display.
 */
function getBrokerLoginUrl(broker: string): string | null {
  const key = broker.trim().toLowerCase();
  if (key.includes("alpaca")) return "https://app.alpaca.markets/login";
  if (key.includes("interactive")) {
    return "https://www.interactivebrokers.com/sso/Login";
  }
  if (key.includes("whitehat") || key.includes("white hat")) {
    return "https://app.whitehatbots.com/";
  }
  return null;
}

/**
 * "Verified" green label used next to Email + Phone. Matches the
 * Robinhood treatment — a small green wordmark, not a chip — so the
 * row still reads as data rather than as an action.
 */
function VerifiedChip() {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <CheckCircleRounded sx={{ fontSize: 14, color: "success.main" }} />
      <Typography
        variant="caption"
        sx={{ color: "success.main", fontWeight: 700, fontSize: 12 }}
      >
        Verified
      </Typography>
    </Stack>
  );
}

